import {
  ActionReducerMapBuilder,
  AsyncThunk,
  createAction,
  CaseReducer,
} from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { TypedActionCreator } from "@reduxjs/toolkit/dist/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/dist/tsHelpers";
import { PayloadActionCreator } from "@reduxjs/toolkit/dist/createAction";
import {
  ECommState,
  ICommunication,
  IStateWithCommunication,
} from "../types/redux";
import Api from "../api";
import { AxiosError, AxiosResponse } from "axios";
import {
  ErrorsData,
  ErrorsWithDetails,
  IDataErrorResponse,
} from "../api/types/response";

const pendingCommunication: ICommunication = { state: ECommState.pending };
const rejectedCommunication: ICommunication = { state: ECommState.rejected };
const fulfilledCommunication: ICommunication = { state: ECommState.fulfilled };

export const initialCommunication: ICommunication = { state: ECommState.clear };

type TStateWithCommunication<State> = State & IStateWithCommunication;

export type TPhaseAction<Payload, Result, Err extends Error = Error> = {
  pending: PayloadActionCreator<Payload>;
  fulfilled: PayloadActionCreator<Result>;
  rejected: PayloadActionCreator<Err>;
} & PayloadActionCreator<Payload>;

export interface ICommunicationReducerMap<State> {
  pending?: (state: TStateWithCommunication<State>, action: AnyAction) => State;
  rejected?: (
    state: TStateWithCommunication<State>,
    action: AnyAction
  ) => State;
  fulfilled?: (
    state: TStateWithCommunication<State>,
    action: AnyAction
  ) => State;
}

export function communicationChannel<
  Returned,
  ThunkArg,
  ThunkApiConfig,
  State extends IStateWithCommunication,
  ActionCreator extends TypedActionCreator<string>
>(
  thunk:
    | AsyncThunk<Returned, ThunkArg, ThunkApiConfig>
    | TPhaseAction<ThunkArg, Returned>,
  builder: ActionReducerMapBuilder<NoInfer<TStateWithCommunication<State>>>,
  propName: keyof TStateWithCommunication<State>["communication"],
  reducersMap?: ICommunicationReducerMap<State>
): void {
  const emptyReducerProxy = (state: TStateWithCommunication<State>) => state;
  const pendingReducerProxy = reducersMap?.pending || emptyReducerProxy;
  const rejectedReducerProxy = reducersMap?.rejected || emptyReducerProxy;
  const fulfilledReducerProxy = reducersMap?.fulfilled || emptyReducerProxy;

  const pendingReducer: CaseReducer<State, ReturnType<ActionCreator>> = (
    state,
    action
  ) => {
    return pendingReducerProxy(
      {
        ...state,
        communication: {
          ...state.communication,
          [propName]: pendingCommunication,
        },
      } as State,
      action
    );
  };

  builder.addCase(thunk.pending.toString(), pendingReducer);

  const rejectReducer: CaseReducer<State, ReturnType<ActionCreator>> = (
    state,
    action
  ) => {
    // Catch rejected communication channel here
    return rejectedReducerProxy(
      {
        ...state,
        communication: {
          ...state.communication,
          [propName]: {
            ...rejectedCommunication,
            error: (action as AnyAction).payload,
          },
        },
      } as State,
      action
    );
  };

  builder.addCase(thunk.rejected.toString(), rejectReducer);

  const fulfilledReducer: CaseReducer<State, ReturnType<ActionCreator>> = (
    state,
    action
  ) => {
    return fulfilledReducerProxy(
      {
        ...state,
        communication: {
          ...state.communication,
          [propName]: fulfilledCommunication,
        },
      } as State,
      action
    );
  };

  builder.addCase(thunk.fulfilled.toString(), fulfilledReducer);
}

export function createPhaseAction<ResultType = void, PayloadType = void>(
  typePrefix: string
): TPhaseAction<PayloadType, ResultType> {
  const pending = createAction<PayloadType>(`${typePrefix}/pending`);
  const fulfilled = createAction<ResultType>(`${typePrefix}/fulfilled`);
  const rejected = createAction(`${typePrefix}/rejected`);

  return Object.assign(pending, {
    pending,
    fulfilled,
    rejected,
  }) as typeof pending & {
    pending: typeof pending;
    fulfilled: typeof fulfilled;
    rejected: typeof rejected;
  };
}

type TResponseErrorInfo =
  | AxiosError<IDataErrorResponse>
  | AxiosResponse<IDataErrorResponse>;

function getErrorData(
  target: TResponseErrorInfo
): IDataErrorResponse | undefined {
  return Api.hasApiError(target)
    ? (target as AxiosError<IDataErrorResponse>).response.data
    : (target as AxiosResponse<IDataErrorResponse>).data;
}

export function hasResponseErrorCode(target: TResponseErrorInfo): boolean {
  const error = getErrorData(target)?.error;
  return (
    (error as ErrorsWithDetails)?.errorDetail?.errorCode > 0 ||
    (error as ErrorsData)?.code > 0
  );
}

export function removeErrorTitle(text: string | undefined): string | undefined {
  if (!text) {
    return text;
  }

  let result = text;
  result = result.replace(/[^а-яёА-ЯЁ .-]/g, "");
  result = result.trimStart();

  return result;
}

export function getErrorMessage(target: TResponseErrorInfo): string {
  const error = getErrorData(target)?.error;
  const errorMessage =
    (error as ErrorsWithDetails)?.errorDetail?.errorMessage ||
    (error as ErrorsData)?.message ||
    "";

  return removeErrorTitle(errorMessage);
}

export function throwErrorMessageFromResponse(
  target: TResponseErrorInfo,
  cb?: () => void
) {
  if (hasResponseErrorCode(target)) {
    const errorData = {
      errorMessage: getErrorMessage(target),
    };
    cb?.();
    throw Error(JSON.stringify(errorData));
  }
}

export function parceErrorData(error: Error): Array<string | boolean> {
  try {
    return [JSON.parse(error.message).errorMessage, true];
  } catch (err) {
    return [error.message, false];
  }
}
